import { allCommands } from "app/client/components/commands";
import { GristDoc } from "app/client/components/GristDoc";
import { loadSearch } from "app/client/lib/imports";
import { makeT } from "app/client/lib/localization";
import {
  getRegionalCapabilitiesObs,
  isRegionalStructureEditor,
  isRegionalValidationAvailable,
} from "app/client/lib/RegionalMode";
import { AppModel, reportError } from "app/client/models/AppModel";
import { DocPageModel } from "app/client/models/DocPageModel";
import { ViewSectionRec } from "app/client/models/DocModel";
import { urlState } from "app/client/models/gristUrlState";
import { workspaceName } from "app/client/models/WorkspaceInfo";
import { AccountWidget } from "app/client/ui/AccountWidget";
import { buildActiveUserList } from "app/client/ui/ActiveUserList";
import { buildLanguageMenu } from "app/client/ui/LanguageMenu";
import { buildNotifyMenuButton } from "app/client/ui/NotifyUI";
import { manageTeamUsersApp } from "app/client/ui/OpenUserManager";
import { UpgradeButton } from "app/client/ui/ProductUpgrades";
import { buildShareMenuButton } from "app/client/ui/ShareMenu";
import { SupportGristButton } from "app/client/ui/SupportGristButton";
import { hoverTooltip } from "app/client/ui/tooltips";
import { cssHoverCircle, cssTopBarBtn } from "app/client/ui/TopBarCss";
import { docBreadcrumbs } from "app/client/ui2018/breadcrumbs";
import { basicButton, basicButtonLink } from "app/client/ui2018/buttons";
import { isNarrowScreenObs, testId, theme } from "app/client/ui2018/cssVars";
import { IconName } from "app/client/ui2018/IconList";
import {
  RegionalValidationCheck,
  RegionalValidationCheckSummary,
  RegionalValidationStatus,
} from "app/common/UserAPI";
import * as roles from "app/common/roles";

import {
  Computed,
  Disposable,
  dom,
  DomElementArg,
  makeTestId,
  MultiHolder,
  Observable,
  styled,
  subscribe,
} from "grainjs";

import type * as searchModule from "app/client/ui2018/search";

const t = makeT("TopBar");

// Скільки опитів статусу поспіль може впасти, перш ніж це вважається збоєм, а не гикавкою.
// Крок опитування — 1200 мс, тож це близько чотирьох секунд мовчання.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const REGIONAL_IDLE_STATUS: RegionalValidationStatus = {
  status: "idle",
  message: "",
  table_id: null,
  table_title: null,
  page_id: null,
  started_at: null,
  finished_at: null,
  result: null,
  error: "",
};

function _regionalValidationStatusText(status: RegionalValidationStatus): string {
  if (status.error) {
    return t("Перевірка завершилась з помилкою.");
  }
  if (status.message) {
    const lowerMessage = status.message.toLowerCase();
    if (
      lowerMessage.includes("client error") ||
      lowerMessage.includes("unprocessable entity") ||
      lowerMessage.includes("httpstatuserror")
    ) {
      return t("Перевірка завершилась з помилкою.");
    }
    return status.message;
  }
  return t("Manual validation after paste.");
}

function _regionalValidationStatusDetails(status: RegionalValidationStatus): string {
  const fallback = status.error || status.message || _regionalValidationStatusText(status);
  const result = status.result;
  if (!result) {
    return fallback;
  }
  const lines = [fallback];
  const summary = result.table_check_summary as RegionalValidationCheckSummary | undefined;
  if (summary) {
    lines.push(
      [
        `Помилки: ${summary.fail ?? 0}`,
        `Попередження: ${summary.warn ?? 0}`,
        `Успішно: ${summary.pass ?? 0}`,
        `Н/З: ${summary.na ?? 0}`,
      ].join(" | "),
    );
  }
  const invalidRows = typeof result.invalid_rows === "number" ? result.invalid_rows : null;
  if (invalidRows && invalidRows > 0) {
    lines.push(`Некоректні рядки: ${invalidRows}`);
  }
  const checks = Array.isArray(result.table_checks)
    ? (result.table_checks as RegionalValidationCheck[])
        .filter((check) => check.status === "fail" || check.status === "warn")
        .slice(0, 5)
    : [];
  for (const check of checks) {
    lines.push(`${check.status === "fail" ? "Помилка" : "Попередження"}: ${check.message}`);
  }
  return lines.join("\n");
}

export function createTopBarHome(appModel: AppModel, onSave?: (personal: boolean) => Promise<unknown>) {
  const isAnonymous = !appModel.currentValidUser;

  return [
    cssFlexSpace(),
    cssButtons(
      dom.create(UpgradeButton, appModel),
      dom.create(SupportGristButton, appModel),
      (appModel.isTeamSite && roles.canEditAccess(appModel.currentOrg?.access || null) ?
        [
          basicButton(
            t("Manage team"),
            dom.on("click", () => manageTeamUsersApp({ app: appModel, onSave })),
            testId("topbar-manage-team"),
          ),
        ] :
        null
      ),
    ),
    buildLanguageMenu(appModel),
    isAnonymous ? null : buildNotifyMenuButton(appModel.notifier, appModel),
    dom("div", dom.create(AccountWidget, appModel)),
  ];
}

export function createTopBarDoc(owner: MultiHolder, appModel: AppModel, pageModel: DocPageModel) {
  const doc = pageModel.currentDoc;
  const regionalCaps = getRegionalCapabilitiesObs(pageModel.appModel);
  const renameDoc = (val: string) => pageModel.renameDoc(val);
  const displayNameWs = Computed.create(owner, pageModel.currentWorkspace,
    (use, ws) => ws ? { ...ws, name: workspaceName(appModel, ws) } : ws);

  const moduleObs = Observable.create<typeof searchModule | null>(owner, null);
  loadSearch().then(module => moduleObs.set(module)).catch(reportError);

  // Observable to decide whether to include the searchBar into this page. It doesn't work on
  // 'code' and 'acl' pages, so it's better to omit it, and let the browser's native search work.
  const enabledObs = Computed.create(owner, pageModel.gristDoc, (use, gristDoc) => {
    const viewId = gristDoc ? use(gristDoc.activeViewId) : null;
    return viewId !== null && viewId !== "code" && viewId !== "acl";
  });

  const searchModelObs = Computed.create(owner,
    moduleObs, pageModel.gristDoc, enabledObs,
    (use, module, gristDoc, enabled) => {
      if (!module || !gristDoc || !enabled) {
        return null;
      }
      return module.SearchModelImpl.create(use.owner, gristDoc);
    });

  const isSearchOpen = Computed.create(owner, searchModelObs, (use, searchModel) => {
    return Boolean(searchModel && use(searchModel.isOpen));
  });

  const isUndoRedoAvailable = Computed.create(owner, (use) => {
    const gristDoc = use(pageModel.gristDoc);
    if (!gristDoc) { return false; }

    const undoStack = gristDoc.getUndoStack();
    return !use(undoStack.isDisabled);
  });

  const isAnonymous = !pageModel.appModel.currentValidUser;

  return [
    // TODO Before gristDoc is loaded, we could show doc-name without the page. For now, we delay
    // showing of breadcrumbs until gristDoc is loaded.
    dom.maybe(pageModel.gristDoc, gristDoc =>
      cssBreadcrumbContainer(
        docBreadcrumbs(displayNameWs, pageModel.currentDocTitle, gristDoc.currentPageName, {
          docNameSave: renameDoc,
          pageNameSave: getRenamePageFn(gristDoc),
          cancelRecoveryMode: getCancelRecoveryModeFn(gristDoc),
          isPageNameReadOnly: use =>
            use(gristDoc.isReadonly) ||
            typeof use(gristDoc.activeViewId) !== "number" ||
            !isRegionalStructureEditor(use(regionalCaps)),
          isDocNameReadOnly: use => use(gristDoc.isReadonly) || use(pageModel.isFork),
          isFork: pageModel.isFork,
          isBareFork: pageModel.isBareFork,
          isRecoveryMode: pageModel.isRecoveryMode,
          isTutorialFork: pageModel.isTutorialFork,
          isFiddle: Computed.create(owner, use => use(pageModel.isPrefork)),
          isSnapshot: pageModel.isSnapshot,
          isPublic: Computed.create(owner, doc, (use, _doc) => Boolean(_doc?.public)),
          isTemplate: pageModel.isTemplate,
          isAnonymous,
          isProposable: Computed.create(
            owner, gristDoc.docPageModel.currentDoc,
            (_use, currentDoc) => Boolean(currentDoc?.options?.proposedChanges?.acceptProposals),
          ),
          isReadonly: pageModel.isReadonly,
          proposeChanges: async () => {
            const { urlId } = await gristDoc.docComm.fork();
            await urlState().pushUrl({ doc: urlId });
          },
        }),
        dom.hide(use => use(isSearchOpen) && use(isNarrowScreenObs())),
      ),
    ),
    cssFlexSpace(),
    dom.maybe(pageModel.gristDoc, gristDoc => buildActiveUserList(owner, gristDoc.userPresenceModel)),
    // Don't show useless undo/redo buttons for sample docs, to leave more space for "Make copy".
    dom.maybe(pageModel.undoState, state => [
      topBarUndoBtn("Undo",
        dom.on("click", () => state.isUndoDisabled.get() || allCommands.undo.run()),
        dom.hide(use => use(isSearchOpen)),
        hoverTooltip(t("Undo"), { key: "topBarBtnTooltip" }),
        cssHoverCircle.cls("-disabled", use => use(state.isUndoDisabled) || !use(isUndoRedoAvailable)),
        dom.attr("aria-disabled", use => use(state.isUndoDisabled) ? "true" : "false"),
        dom.attr("aria-label", t("Undo")),
        testId("undo"),
      ),
      topBarUndoBtn("Redo",
        dom.on("click", () => state.isRedoDisabled.get() || allCommands.redo.run()),
        dom.hide(use => use(isSearchOpen)),
        hoverTooltip(t("Redo"), { key: "topBarBtnTooltip" }),
        cssHoverCircle.cls("-disabled", use => use(state.isRedoDisabled) || !use(isUndoRedoAvailable)),
        dom.attr("aria-disabled", use => use(state.isRedoDisabled) ? "true" : "false"),
        dom.attr("aria-label", t("Redo")),
        testId("redo"),
      ),
      cssSpacer(),
    ]),
    dom.domComputed((use) => {
      const model = use(searchModelObs);
      return model && use(moduleObs)?.searchBar(
        model, makeTestId("test-tb-search-"),
        pageModel.gristDoc.get()?.regionFocusSwitcher,
      );
    }),
    dom.maybe(use => !(use(pageModel.isTemplate) && isAnonymous), () => [
      // Admin-only entry to the schema & dictionaries editor (served via the admin-gated proxy).
      dom.domComputed((use) => isRegionalStructureEditor(use(regionalCaps))
        ? basicButtonLink(
            t("Адмін: схема"),
            dom.attr("href", "/api/regional/admin/ui/"),
            dom.attr("target", "_blank"),
            testId("regional-admin-link"),
          )
        : null),
      dom.create(RegionalValidationToolbar, pageModel),
      buildShareMenuButton(pageModel),
      dom.domComputed((use) => {
        if (use(regionalCaps).role === "regional_user") {
          return null;
        }
        const gristDoc = use(pageModel.gristDoc);
        return gristDoc ? buildShowDiscussionButton(gristDoc) : null;
      }),
      buildNotifyMenuButton(appModel.notifier, appModel),
    ]),
    dom("div", dom.create(AccountWidget, appModel, pageModel)),
  ];
}

interface RegionalValidationContext {
  docId: string;
  pageId: number;
  tableId: string;
}

function _isRegionalValidationSection(section: ViewSectionRec): boolean {
  const parentKey = section.parentKey.peek();
  return parentKey === "record" || parentKey === "detail" || parentKey === "single";
}

function _getRegionalValidationTableId(
  use: (obs: any) => any,
  section: ViewSectionRec | null | undefined,
  pageId: number,
): string | null {
  if (!section || section.isDisposed() || !section.getRowId() || !_isRegionalValidationSection(section)) {
    return null;
  }
  if (use(section.parentId) !== pageId) {
    return null;
  }
  const table = use(section.table);
  if (!table || table.isDisposed() || !table.getRowId()) {
    return null;
  }
  const tableId = use(table.tableId);
  return tableId ? String(tableId) : null;
}

function _getRegionalValidationContextForPage(
  use: (obs: any) => any,
  gristDoc: GristDoc,
  pageId: number,
): Pick<RegionalValidationContext, "pageId" | "tableId"> | null {
  const view = gristDoc.docModel.views.getRowModel(pageId);
  if (!view || view.isDisposed() || !view.getRowId()) {
    return null;
  }

  const viewSections = use(view.viewSections);
  const sections = viewSections?.all ? viewSections.all() as ViewSectionRec[] : [];
  const currentView = use(gristDoc.currentView);
  const candidates = [
    use(view.activeSection) as ViewSectionRec,
    currentView?.viewSection as ViewSectionRec | undefined,
    ...sections,
  ];
  const seen = new Set<number>();
  for (const section of candidates) {
    const sectionId = section?.getRowId();
    if (!sectionId || seen.has(sectionId)) {
      continue;
    }
    seen.add(sectionId);
    const tableId = _getRegionalValidationTableId(use, section, pageId);
    if (tableId) {
      return { pageId, tableId };
    }
  }
  return null;
}

class RegionalValidationToolbar extends Disposable {
  private readonly _caps = getRegionalCapabilitiesObs(this._pageModel.appModel);
  private readonly _status = Observable.create<RegionalValidationStatus>(this, REGIONAL_IDLE_STATUS);
  private readonly _isSubmitting = Observable.create(this, false);
  private readonly _isRefreshing = Observable.create(this, false);
  private readonly _statusByScope = new Map<string, RegionalValidationStatus>();
  private readonly _context = Computed.create<RegionalValidationContext | null>(this, (use) => {
    const caps = use(this._caps);
    if (!isRegionalValidationAvailable(caps)) {
      return null;
    }
    const gristDoc = use(this._pageModel.gristDoc);
    if (!gristDoc) {
      return null;
    }
    const pageId = use(gristDoc.activeViewId);
    if (typeof pageId !== "number") {
      return null;
    }
    const tableContext = _getRegionalValidationContextForPage(use, gristDoc, pageId);
    if (!tableContext) {
      return null;
    }
    const docId = use(this._pageModel.currentDocId);
    if (!docId) {
      return null;
    }
    return { docId, ...tableContext };
  });
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _pollScopeKey: string | null = null;
  private _requestToken = 0;
  private _pollFailures = 0;

  constructor(private readonly _pageModel: DocPageModel) {
    super();
    this.onDispose(() => this._stopPolling());
    this.autoDispose(subscribe(this._context, async (_use, context) => {
      const requestToken = ++this._requestToken;
      this._stopPolling();
      if (!context) {
        this._isRefreshing.set(false);
        this._status.set(REGIONAL_IDLE_STATUS);
        return;
      }
      const cachedStatus = this._statusByScope.get(this._contextKey(context));
      this._status.set(cachedStatus ?? {
        ...REGIONAL_IDLE_STATUS,
        message: t("Панель перевірки завантажується."),
        table_id: context.tableId,
        page_id: context.pageId,
      });
      this._isRefreshing.set(true);
      await this._refreshStatus(context, requestToken);
    }));
  }

  public buildDom() {
    return dom.domComputed((use) => {
      const caps = use(this._caps);
      const gristDoc = use(this._pageModel.gristDoc);
      const context = use(this._context);
      if (!isRegionalValidationAvailable(caps) || !context) {
        if (!isRegionalValidationAvailable(caps) || !gristDoc) {
          return null;
        }
        // REGIONAL FORK: на сторінці без таблиці (правила доступу, вихідні дані, код, дашборд)
        // перевіряти нічого, а сіра кнопка з «Панель перевірки завантажується.» висіла там назавжди.
        // Заглушка — лише для сторінки з таблицею, поки контекст ще не готовий.
        // [[interfeys-rozporiadnyka-bez-mertvykh-elementiv]]
        const pageId = use(gristDoc.activeViewId);
        if (typeof pageId !== "number" || !_getRegionalValidationContextForPage(use, gristDoc, pageId)) {
          return null;
        }
        return cssRegionalValidateWrap(
          basicButton(
            t("Перепровірити таблицю"),
            dom.boolAttr("disabled", true),
            testId("regional-validate"),
          ),
          cssRegionalValidateStatus(
            t("Панель перевірки завантажується."),
          ),
        );
      }
      const status = use(this._status);
      const isRefreshing = use(this._isRefreshing);
      const submitting = use(this._isSubmitting);
      const isRunning = status.status === "queued" || status.status === "running";
      const statusText = isRefreshing && status.status === "idle" && !status.message
        ? t("Панель перевірки завантажується.")
        : _regionalValidationStatusText(status);
      const statusDetails = _regionalValidationStatusDetails(status);
      // Per-phase autofill progress bar [Р24] — shown only while a run is active and reporting.
      const progress = isRunning ? status.progress : null;
      const percent = progress ? Math.min(100, Math.max(0, progress.percent)) : 0;
      return cssRegionalValidateWrap(
        basicButton(
          t("Перепровірити таблицю"),
          dom.on("click", () => this._runValidation()),
          dom.boolAttr("disabled", () => submitting || isRunning),
          testId("regional-validate"),
        ),
        cssRegionalValidateStatus(
          statusText,
          dom.attr("title", statusDetails),
          cssRegionalValidateStatus.cls("-busy", isRunning),
          cssRegionalValidateStatus.cls("-error", Boolean(status.error)),
        ),
        progress ? cssRegionalProgressBar(
          cssRegionalProgressFill(dom.style("width", `${percent}%`)),
          dom.attr("title", `${progress.label} (${progress.index}/${progress.total})`),
          testId("regional-validate-progress"),
        ) : null,
      );
    });
  }

  private async _runValidation() {
    const context = this._context.get();
    if (!context || this._isSubmitting.get()) {
      return;
    }
    this._isSubmitting.set(true);
    const requestToken = ++this._requestToken;
    try {
      const gristDoc = this._pageModel.gristDoc.get();
      const activeEditor = gristDoc?.activeEditor.get();
      if (activeEditor && !activeEditor.isDisposed()) {
        const activeEditorDom = activeEditor.getDom();
        if (activeEditorDom?.isConnected) {
          await activeEditor.save();
        }
      }
      const docApi = this._pageModel.appModel.api.getDocAPI(context.docId);
      const status = await docApi.runRegionalValidation(context.tableId, { pageId: context.pageId });
      this._applyStatus(context, status, requestToken);
    } catch (err) {
      reportError(err);
      this._applyStatus(context, {
        ...REGIONAL_IDLE_STATUS,
        status: "failed",
        error: err instanceof Error ? err.message : String(err),
        table_id: context.tableId,
        page_id: context.pageId,
      }, requestToken);
    } finally {
      if (!this.isDisposed()) {
        this._isSubmitting.set(false);
      }
    }
  }

  private async _refreshStatus(context: RegionalValidationContext, requestToken: number) {
    try {
      const docApi = this._pageModel.appModel.api.getDocAPI(context.docId);
      const status = await docApi.getRegionalValidationStatus({
        pageId: context.pageId,
        tableId: context.tableId,
      });
      this._pollFailures = 0;
      this._applyStatus(context, status, requestToken);
    } catch (err) {
      // Один невдалий опит — ще не подія, і поводитись із ним як з подією виходило дорого одразу
      // двічі. По-перше, кожен піднімав власний тост «Unexpected error / Failed to fetch», а за
      // довгу перевірку опитів сотні. По-друге — і це гірше — він записував статус `failed`, а
      // `failed` не є `running`, тож `_applyStatus` глушив опитування НАЗОВСІМ: одна мережева
      // гикавка посеред перевірки, і банер завмирав на «виконується», більше нічого не питаючи,
      // а кнопка лишалась заблокованою до перезавантаження сторінки.
      //
      // Тож тримаємо попередній статус і пробуємо далі. Говоримо вголос лише тоді, коли зв'язок
      // не відновився за кілька спроб поспіль — тобто коли це справді подія. [street-lookup-budget]
      if (++this._pollFailures < MAX_CONSECUTIVE_POLL_FAILURES) {
        if (!this.isDisposed()) {
          this._isRefreshing.set(false);
        }
        return;
      }
      reportError(err);
      this._applyStatus(context, {
        ...REGIONAL_IDLE_STATUS,
        status: "failed",
        error: err instanceof Error ? err.message : String(err),
        table_id: context.tableId,
        page_id: context.pageId,
      }, requestToken);
    }
  }

  private _applyStatus(
    context: RegionalValidationContext,
    status: RegionalValidationStatus,
    requestToken: number,
  ) {
    if (this.isDisposed()) {
      return;
    }
    const scopeKey = this._contextKey(context);
    this._statusByScope.set(scopeKey, status);
    const activeContext = this._context.get();
    const isActiveScope = activeContext ? this._contextKey(activeContext) === scopeKey : false;
    if (isActiveScope && requestToken === this._requestToken) {
      this._status.set(status);
      this._isRefreshing.set(false);
    }
    if (isActiveScope && (status.status === "queued" || status.status === "running")) {
      this._ensurePolling(context);
    } else if (this._pollScopeKey === scopeKey) {
      this._stopPolling();
    }
  }

  private _ensurePolling(context: RegionalValidationContext) {
    const scopeKey = this._contextKey(context);
    if (this._pollTimer && this._pollScopeKey === scopeKey) {
      return;
    }
    this._stopPolling();
    this._pollScopeKey = scopeKey;
    this._pollFailures = 0;
    const tick = async () => {
      this._pollTimer = null;
      if (this.isDisposed()) {
        return;
      }
      const activeContext = this._context.get();
      if (!activeContext || this._contextKey(activeContext) !== scopeKey) {
        this._pollScopeKey = null;
        return;
      }
      const requestToken = ++this._requestToken;
      this._isRefreshing.set(true);
      await this._refreshStatus(activeContext, requestToken);
      const currentStatus = this._statusByScope.get(scopeKey)?.status;
      if (!this.isDisposed() && this._pollScopeKey === scopeKey &&
        (currentStatus === "queued" || currentStatus === "running")) {
        this._pollTimer = setTimeout(() => void tick(), 1200);
      }
    };
    this._pollTimer = setTimeout(() => void tick(), 1200);
  }

  private _stopPolling() {
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
    this._pollScopeKey = null;
  }

  private _contextKey(context: RegionalValidationContext) {
    return [context.docId, context.pageId, context.tableId].join("::");
  }
}

function buildShowDiscussionButton(gristDoc: GristDoc) {
  return cssHoverCircle({ style: `margin: 5px; position: relative;` },
    cssTopBarBtn("Chat", dom.cls("tour-share-icon")),
    hoverTooltip("Comments", { key: "topBarBtnTooltip" }),
    testId("open-discussion"),
    dom.on("click", () => {
      gristDoc.showTool("discussion");
      allCommands.rightPanelOpen.run();
    }),
  );
}

// Given the GristDoc instance, returns a rename function for the current active page.
// If the current page is not able to be renamed or the new name is invalid, the function is a noop.
function getRenamePageFn(gristDoc: GristDoc): (val: string) => Promise<void> {
  return async (val: string) => {
    if (!isRegionalStructureEditor(getRegionalCapabilitiesObs(gristDoc.app.topAppModel).get())) {
      return;
    }
    const views = gristDoc.docModel.views;
    const viewId = gristDoc.activeViewId.get();
    if (typeof viewId === "number" && val.length > 0) {
      const name = views.rowModels[viewId].name;
      await name.saveOnly(val);
    }
  };
}

function getCancelRecoveryModeFn(gristDoc: GristDoc): () => Promise<void> {
  return async () => {
    await gristDoc.app.topAppModel.api.getDocAPI(gristDoc.docPageModel.currentDocId.get()!)
      .recover(false);
  };
}

function topBarUndoBtn(iconName: IconName, ...domArgs: DomElementArg[]): Element {
  return cssHoverCircle(
    cssTopBarUndoBtn(iconName),
    ...domArgs,
  );
}

const cssButtons = styled("div", `
  display: flex;
  gap: 8px;
  margin-right: 8px;
`);

const cssTopBarUndoBtn = styled(cssTopBarBtn, `
  background-color: ${theme.topBarButtonSecondaryFg};

  .${cssHoverCircle.className}:hover & {
    background-color: ${theme.topBarButtonPrimaryFg};
  }

  .${cssHoverCircle.className}-disabled:hover & {
    background-color: ${theme.topBarButtonDisabledFg};
    cursor: default;
  }
`);

const cssBreadcrumbContainer = styled("div", `
  padding: 7px;
  flex: 1 1 auto;
  min-width: 24px;
  overflow: hidden;
`);

const cssFlexSpace = styled("div", `
  flex: 1 1 0px;
`);

const cssSpacer = styled("div", `
  max-width: 10px;
  flex: auto;
`);

const cssRegionalValidateWrap = styled("div", `
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 12px;
  min-width: 0;
`);

const cssRegionalValidateStatus = styled("div", `
  font-size: 12px;
  line-height: 1.35;
  color: ${theme.lightText};
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &-busy {
    color: ${theme.topBarButtonPrimaryFg};
  }

  &-error {
    color: ${theme.errorText};
  }
`);

// Determinate progress bar for the autofill/validation run [Р24].
const cssRegionalProgressBar = styled("div", `
  flex: none;
  width: 96px;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background-color: ${theme.hover};
`);

const cssRegionalProgressFill = styled("div", `
  height: 100%;
  border-radius: 3px;
  background-color: ${theme.topBarButtonPrimaryFg};
  transition: width 0.2s ease;
`);
